import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { BillingAddress, BillingAddressInput } from '@jobos/shared/types/billingAddress';
import { useToast } from '@/hooks/use-toast';
import { clientKeys } from '@/hooks/useClients';

interface BillingAddressesResponse {
  billingAddresses: BillingAddress[];
}

interface BillingAddressResponse {
  billingAddress: BillingAddress;
}

export const billingAddressKeys = {
  all: ['billing-addresses'] as const,
  list: (clientId: string) => [...billingAddressKeys.all, 'list', clientId] as const,
};

export function useBillingAddresses(clientId: string | undefined) {
  return useQuery({
    queryKey: billingAddressKeys.list(clientId!),
    queryFn: async () => {
      const response = await apiClient.get<BillingAddressesResponse>(
        `/billing-addresses?clientId=${clientId}`
      );
      return response.billingAddresses;
    },
    enabled: !!clientId,
  });
}

export function useCreateBillingAddress() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: BillingAddressInput) => {
      const response = await apiClient.post<BillingAddressResponse>('/billing-addresses', data);
      return response.billingAddress;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: billingAddressKeys.list(variables.clientId) });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.clientId) });
      toast({ title: 'Success', description: 'Billing address created' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to create billing address', variant: 'destructive' });
    },
  });
}

export function useUpdateBillingAddress(clientId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const response = await apiClient.put<BillingAddressResponse>(`/billing-addresses/${id}`, data);
      return response.billingAddress;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingAddressKeys.list(clientId) });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) });
      toast({ title: 'Success', description: 'Billing address updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to update billing address', variant: 'destructive' });
    },
  });
}

export function useDeleteBillingAddress(clientId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/billing-addresses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingAddressKeys.list(clientId) });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) });
      toast({ title: 'Success', description: 'Billing address deleted' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete billing address', variant: 'destructive' });
    },
  });
}

export function useSetDefaultBillingAddress(clientId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.put(`/billing-addresses/${id}/default`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingAddressKeys.list(clientId) });
      toast({ title: 'Success', description: 'Default billing address updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to set default', variant: 'destructive' });
    },
  });
}
